<template>
  <div ref="carousel" class="carousel" v-on:touchstart="handleTouchStart" v-on:touchend="handleTouchEnd">
    <Animation
      :duration="{ enter: transitionDuration, leave: transitionDuration }"
      :enter="enterTransition"
      :leave="leaveTransition"
      :easing="transitionEasing"
      group
    >
      <!-- Slides -->
      <template v-for="(slot, i) in slots">
        <CarouselSlide v-if="i === value" :key="slot">
          <slot :name="slot" />
        </CarouselSlide>
      </template>
    </Animation>

    <!-- Controls -->
    <CarouselControls
      v-if="!!controls"
      :count="slots.length"
      :value="value"
      :orientation="orientation"
      :position="controlsPosition"
      class="carousel__controls"
      @input="$emit('input', $event)"
    />

    <!-- Arrows -->
    <CarouselArrows v-if="!!arrows" :count="slots.length" :value="value" :color="arrowsColor" :orientation="orientation" :loop="loop" @input="$emit('input', $event)" />
  </div>
</template>

<script>
import { computed, onMounted, ref, toRefs, useSlots, watch } from '@vue/composition-api';
import { useCarouselTransitions, useCarouselAutoSlide } from '@/lego/composables/animations';
import { onEnter, onLeave } from '@/lego/utils/dom/element';
import CarouselSlide from '@/lego/components/carousels/CarouselSlide.vue';
import CarouselControls from '@/lego/components/carousels/CarouselControls.vue';
import CarouselArrows from '@/lego/components/carousels/CarouselArrows.vue';
import Animation from '@/lego/components/tools/Animation.vue';

export default {
  components: { CarouselSlide, CarouselControls, CarouselArrows, Animation },

  props: {
    value: {
      type: Number,
      required: true,
    },

    controls: {
      type: Boolean,
      default: true,
    },

    controlsPosition: {
      type: String,
      default: 'end',
    },

    arrows: {
      type: Boolean,
      default: true,
    },

    arrowsColor: {
      type: String,
      default: 'white',
    },

    transition: {
      type: String,
      default: 'slide',
    },

    transitionDuration: {
      type: Number,
      default: 350,
    },

    transitionEasing: {
      type: String,
      default: 'inherit',
    },

    orientation: {
      type: String,
      default: 'horizontal',
    },

    auto: {
      type: Number,
      default: 0,
    },

    pauseOnHover: {
      type: Boolean,
      default: false,
    },

    loop: {
      type: Boolean,
      default: false,
    },

    touchScroll: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const carousel = ref(null);
    const timer = ref(null);
    const slots = computed(() => Object.keys(useSlots()));
    const initialTouch = ref(null);
    const { value: model, transition, transitionDuration, orientation, auto } = toRefs(props);

    const { enterTransition, leaveTransition } = useCarouselTransitions(model, transition, orientation);
    const { restartAutoSlide, pauseAutoSlide, onCarouselSlide } = useCarouselAutoSlide(auto);

    const restart = () => {
      restartAutoSlide();

      emit('slide:start');
      timer.value = setTimeout(() => emit('slide:end'), transitionDuration.value);
    };

    watch(model, () => {
      restart();
    });

    onCarouselSlide(() => {
      if (model.value < slots.value.length - 1) {
        emit('input', model.value + 1);
      } else {
        emit('input', 0);
      }
    });

    onMounted(() => {
      if (props.pauseOnHover) {
        onEnter(carousel.value, () => {
          pauseAutoSlide();

          clearTimeout(timer.value);
        });
  
        onLeave(carousel.value, () => {
          restart();
        });
      }
    });

    const handleTouchStart = (e) => {
      initialTouch.value = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      const diff = initialTouch.value - e.changedTouches[0].clientX;

      if (!props.touchScroll) {
        return;
      }

      if (diff > 0) {
        emit('input', (model.value < slots.value.length - 1) ? model.value + 1 : 0);
      } else {
        emit('input', model.value > 0 ? model.value - 1 : slots.value.length - 1);
      }
    };

    return { carousel, slots, enterTransition, leaveTransition, handleTouchStart, handleTouchEnd };
  },
};
</script>

<style scoped lang="postcss">
.carousel {
  @apply relative overflow-hidden;

  &__controls {
    @apply z-10;
  }
}
</style>
